import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  Divider,
  TextField,
} from "@mui/material";

import { CloudUpload, Image as ImageIcon, Delete } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import React, { useCallback, useEffect, useMemo, useState } from "react";

// dnd-kit
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export type ImageItem = {
  id: string;
  imagePath: string; // ✅ รูปเก่า: url เต็ม / รูปใหม่: ว่างจนกว่าจะอัปโหลด
  file?: File;       // ✅ มีเฉพาะรูปใหม่
  title: string;
  orderSeq: number;
};

export type ImageUploadTabProps = {
  maxFiles?: number;
  value?: ImageItem[];
  onChange?: (items: ImageItem[]) => void;
};

type InternalItem = ImageItem & {
  previewUrl?: string; // objectURL สำหรับ file ใหม่
};

function SortableItem({
  item,
  index,
  onRemove,
  onTitleChange,
}: {
  item: InternalItem;
  index: number;
  onRemove: (index: number) => void;
  onTitleChange: (index: number, title: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: item.id,
    });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const src = item.file ? item.previewUrl : item.imagePath;

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{
        p: 1,
        position: "relative",
        opacity: isDragging ? 0.7 : 1,
        cursor: "grab",
        userSelect: "none",
      }}
      style={style}
      {...attributes}
      {...listeners}
    >
      <Box
        component="img"
        src={src || ""}
        alt={item.title || item.file?.name || "image"}
        sx={{
          width: "100%",
          height: 220,
          objectFit: "cover",
          borderRadius: 1,
          bgcolor: "action.hover",
        }}
      />

      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(index);
        }}
        sx={{
          position: "absolute",
          top: 6,
          right: 6,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Delete fontSize="small" sx={{color : 'red'}} />
      </IconButton>

      <TextField
        size="small"
        placeholder="ใส่คำอธิบายรูป"
        value={item.title}
        onChange={(e) => onTitleChange(index, e.target.value)}
        onPointerDown={(e) => e.stopPropagation()}
        sx={{ mt: 1 }}
        fullWidth
      />
    </Paper>
  );
}

export default function ImageUploadTab({
  maxFiles = 10,
  value,
  onChange,
}: ImageUploadTabProps) {
  const [items, setItems] = useState<InternalItem[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const emitChange = useCallback(
    (next: InternalItem[]) => {
      onChange?.(
        next.map((x, idx) => ({
          id: x.id,
          imagePath: x.imagePath ?? "",
          file: x.file,
          title: x.title ?? "",
          orderSeq: idx,
        }))
      );
    },
    [onChange]
  );

  // sync จาก value (controlled) + สร้าง objectURL สำหรับ item ที่มี file
  useEffect(() => {
    if (!value) return;

    const mapped: InternalItem[] = value.map((x) => {
      const hasFile = !!x.file;
      const previewUrl = hasFile ? URL.createObjectURL(x.file!) : undefined;

      return {
        ...x,
        imagePath: x.imagePath ?? "",
        title: x.title ?? "",
        orderSeq: x.orderSeq ?? 0,
        previewUrl,
      };
    });

    setItems(mapped);

    return () => {
      mapped.forEach((x) => {
        if (x.previewUrl) URL.revokeObjectURL(x.previewUrl);
      });
    };
  }, [value]);

  const onDrop = useCallback(
    (accepted: File[]) => {
      const remaining = maxFiles - items.length;
      if (remaining <= 0) return;

      const next: InternalItem[] = accepted.slice(0, remaining).map((file) => ({
        id: `new-${crypto.randomUUID?.() ?? Math.random()}`,
        imagePath: "",
        file,
        title: "",
        orderSeq: 0,
        previewUrl: URL.createObjectURL(file),
      }));

      setItems((prev) => {
        const merged = [...prev, ...next].map((x, idx) => ({ ...x, orderSeq: idx }));
        emitChange(merged);
        return merged;
      });
    },
    [items.length, maxFiles, emitChange]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".webp"],
    },
  });

  const removeAt = (index: number) => {
    setItems((prev) => {
      const target = prev[index];
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);

      const next = prev
        .filter((_, i) => i !== index)
        .map((x, idx) => ({ ...x, orderSeq: idx }));

      emitChange(next);
      return next;
    });
  };

  const clearAll = () => {
    items.forEach((x) => {
      if (x.previewUrl) URL.revokeObjectURL(x.previewUrl);
    });
    setItems([]);
    onChange?.([]);
  };

  const updateTitle = (index: number, title: string) => {
    setItems((prev) => {
      const next = prev.map((x, i) => (i === index ? { ...x, title } : x));
      emitChange(next);
      return next;
    });
  };

  const ids = useMemo(() => items.map((x) => x.id), [items]);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const oldIndex = prev.findIndex((x) => x.id === active.id);
      const newIndex = prev.findIndex((x) => x.id === over.id);
      const next = arrayMove(prev, oldIndex, newIndex).map((x, idx) => ({
        ...x,
        orderSeq: idx,
      }));

      emitChange(next);
      return next;
    });
  };

  return (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        {...getRootProps()}
        sx={{
          p: 3,
          borderStyle: "dashed",
          borderWidth: 2,
          borderColor: isDragActive ? "primary.main" : "divider",
          bgcolor: isDragActive ? "action.hover" : "background.paper",
        }}
      >
        <input {...getInputProps()} />
        <Stack spacing={1} alignItems="center">
          <CloudUpload fontSize="large" />
          <Typography fontWeight={700}>
            {isDragActive ? "ปล่อยไฟล์เพื่ออัปโหลด" : "ลากรูปมาวาง หรือกดเลือกไฟล์"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            PNG / JPG / WEBP • สูงสุด {maxFiles} รูป
          </Typography>

          <Stack direction="row" spacing={1} pt={1}>
            <Button variant="contained" startIcon={<ImageIcon />} onClick={open}>
              เลือกรูป
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Delete />}
              disabled={items.length === 0}
              onClick={clearAll}
            >
              ล้างทั้งหมด
            </Button>
          </Stack>

          {items.length > 1 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Tip: ลากการ์ดรูปเพื่อสลับลำดับ
            </Typography>
          )}
        </Stack>
      </Paper>

      <Divider />

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          ยังไม่มีรูปที่เลือก
        </Typography>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={ids} strategy={rectSortingStrategy}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 2,
              }}
            >
              {items.map((it, idx) => (
                <SortableItem
                  key={it.id}
                  item={it}
                  index={idx}
                  onRemove={removeAt}
                  onTitleChange={updateTitle}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      )}
    </Stack>
  );
}
